import CloseIcon from "@mui/icons-material/Close";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  useTheme,
} from "@mui/material";
import { readableDate } from "common/dates";
import * as numbers from "common/numbers";
import { extractNameFromEmail } from "common/strings";
import { useSelector } from "react-redux";
import * as persistenceSelectors from "store/selectors/persistence/persistenceSelectors";

const DocumentInfoDialog = ({ document, layer, open, onClose }) => {
  const theme = useTheme();
  const submissionId = useSelector(persistenceSelectors.selectSubmissionId);

  const handleClose = (event) => {
    event?.stopPropagation();
    onClose?.();
  };

  const versionUrl = document.submissionVersionId
    ? `/submissions/${submissionId}/version/${document.submissionVersionId}`
    : null;

  const createdByName =
    document.createdByName ||
    (document.createdBy
      ? extractNameFromEmail(document.createdBy).name
      : "Unknown");

  const formatDate = (dateStr) => {
    if (!dateStr) return "Unknown";
    return readableDate(dateStr, { includeTime: true });
  };

  const evaluations = document.docEnvConfig || [];
  const hasEvaluations = Array.isArray(evaluations) && evaluations.length > 0;

  const layerLabel = layer
    ? numbers.cleanXS({ limit: layer.limit, attachment: layer.attachment })
    : null;

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth={"md"}
      fullWidth
      onClick={(e) => e.stopPropagation()}
    >
      <DialogTitle>
        <IconButton
          onClick={handleClose}
          sx={{
            position: "absolute",
            right: 8,
            top: 8,
            color: (theme) => theme.palette.grey[500],
          }}
        >
          <CloseIcon />
        </IconButton>
        <Stack spacing={1}>
          <Typography variant={"h6"}>
            {document.formattedFilename || document.templateName}
          </Typography>
          <Typography variant={"body2"} color={"text.secondary"}>
            {"Generated by "}
            {createdByName} {formatDate(document.createdAt)}
          </Typography>
          {layerLabel && (
            <Box sx={{ marginTop: "4px" }}>
              <Chip
                label={layerLabel}
                size={"small"}
                variant={"outlined"}
                sx={{
                  borderColor: "#1976d2",
                  color: "#1976d2",
                  fontWeight: 500,
                  fontSize: "0.8125rem",
                }}
              />
            </Box>
          )}
        </Stack>
      </DialogTitle>

      <DialogContent>
        <Stack spacing={3}>
          {hasEvaluations && (
            <Box>
              <TableContainer
                sx={{
                  border: `1px solid ${theme.brand.gray.gainsboro}`,
                  borderRadius: "4px",
                  maxHeight: "400px",
                }}
              >
                <Table size={"small"} stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell
                        sx={{ fontWeight: 600, backgroundColor: "#FAFAFA" }}
                      >
                        {"Name"}
                      </TableCell>
                      <TableCell
                        sx={{ fontWeight: 600, backgroundColor: "#FAFAFA" }}
                      >
                        {"Value"}
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {evaluations.map((evaluation, index) => (
                      <TableRow key={index}>
                        <TableCell>{evaluation.name}</TableCell>
                        <TableCell>{evaluation.value}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          )}

          {/* Submission Version Info */}
          {document.submissionVersionId && document.submissionVersionNumber && (
            <>
              <Divider />
              <Box>
                <Stack
                  direction={"row"}
                  justifyContent={"space-between"}
                  alignItems={"center"}
                  sx={{
                    padding: 1.5,
                    backgroundColor: "#F5F5F5",
                    borderRadius: "4px",
                  }}
                >
                  <Stack spacing={0.5}>
                    <Typography variant={"subtitle2"} fontWeight={600}>
                      {`Based on Version ${document.submissionVersionNumber}`}
                    </Typography>
                    {document.submissionSavedBy && (
                      <Typography variant={"body2"} color={"text.secondary"}>
                        {"Saved by "}
                        {extractNameFromEmail(document.submissionSavedBy).name}
                        {document.submissionSavedAt &&
                          ` on ${formatDate(document.submissionSavedAt)}`}
                      </Typography>
                    )}
                  </Stack>
                  <Button
                    component={"a"}
                    href={versionUrl}
                    target={"_blank"}
                    rel={"noopener noreferrer"}
                    variant={"outlined"}
                    size={"small"}
                    endIcon={<OpenInNewIcon />}
                    sx={{ textTransform: "none", color: "primary.main" }}
                  >
                    {"Open Version"}
                  </Button>
                </Stack>
              </Box>
            </>
          )}
        </Stack>
      </DialogContent>
    </Dialog>
  );
};

export default DocumentInfoDialog;
